/*
 * Created on 02.05.2016
 *
 * Copyright(c) 1995 - 2016 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */

import { Component, ContentChild, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../i18n/language.service';
import { CustomNumberPipe } from '../../pipes/custom-number.pipe';
import { LoggingService } from '../../utils/logging.service';
import { I18N } from '../form.i18n';
import { CustomSelectItem } from './custom-select-item';
import { SelectItem } from './form-select/form-select-item';

const noop = () => {
};

/**
 * Erweitert den NG_VALUE_ACCESSOR Provider um unsere Komponente. Dies ermöglicht ein databinding mit ngModel und formControl.
 */
export const FORM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FormInputComponent),
    multi: true
};

/**
 * Beschreibt den Input-Typ, den die Input-Komponente {@link FormInputComponent} anzeigt.
 * <li> {@link InputType#text}
 * <li> {@link InputType#password}
 * <li> {@link InputType#file}
 * <li> {@link InputType#checkbox}
 * <li> {@link InputType#radio}
 * <li> {@link InputType#textarea}
 * <li> {@link InputType#select}
 */
export enum InputType {
    /**
     * Text-Feld
     */
    text = 'text',
    /**
     * Passwort-Feld
     */
    password = 'password',
    /**
     * Gibt den Dateininhalt einer Datei zurück. <b>Kein Multipartfile</b>!
     * Benutzt {@link FileReader} um die Datei mittels utf-8 zu lesen.
     * Unterstützt <b>keine</b> Binärfiles.
     */
    file = 'file',
    /**
     * Checkbox
     */
    checkbox = 'checkbox',
    /**
     * Radio-Button-Gruppe
     */
    radio = 'radio',
    /**
     * textarea-Feld.
     */
    textarea = 'textarea',
    /**
     * Select-feld.
     */
    select = 'select'
}

/**
 * Objekt von Model-Wert des Radio-Buttons zu Display-Name.
 * Beispiel:
 * <pre>
 * <common-form-input type="radio"
 *     [items]="{MR: 'Herr', MS: 'Frau'}"
 * ></common-form-input>
 * </pre>
 */
export type RadioButtonItems = {[key: string]: string};
/**
 * Allgemeine Input-Komponente, die mehrere Input-Typen unterstützt und in Reactive Forms
 * genutzt werden kann.
 *
 * Es können "beforeInput" und "afterInput"-Templates als &lt;ng-content&gt; übergeben werden um
 * vor bzw. nach dem Input-Element HTML anzuzeigen. z.B.:
 *
 * <pre>
 * <common-form-input [type]="'text'">
 *     <ng-template #beforeInput><button class="mr-2" ibtn="new"></button></ng-template>
 *     <ng-template #afterInput><button class="ml-2" ibtn="search"></button></ng-template>
 * </common-form-input>
 * </pre>
 */
@Component({
    selector: 'common-form-input',
    templateUrl: 'form-input.component.html',
    providers: [FORM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class FormInputComponent implements ControlValueAccessor, OnInit, OnChanges {

    /**
     * Label der Input-Komponente.
     */
    @Input() label: string;
    // type: type string steht hier, damit es in Templates keine Fehler gibt
    // wenn man 'textarea' etc schreibt; der Compiler kriegt das zwar hin,
    // die IDE meist nicht (wird da als Warning angezeigt)
    /**
     * Siehe {@link InputType}.
     */
    @Input() type: InputType | string;
    /**
     * Name des Inputs, falls gewünscht.
     */
    @Input() name: string;
    /**
     * Die Items für eine Radio-Group oder für eine SelectBox.
     * Siehe {@link SelectItem} oder {@link RadioButtonItems}.
     */
    @Input() items: SelectItem[] | RadioButtonItems | CustomSelectItem[];
    /**
     * Wichtig bei Radio-Group: gibt an ob vertikal('vertical') oder horizontal('inline') angezeigt werden soll.
     * Ansonsten xsmal, smal, large oder x-large falls gewünscht angeben.
     */
    @Input() class: string = '';
    /**
     * Name des Properties in {@link SelectItem} für den Display-Value.
     */
    @Input() displayValue: string;
    /**
     * Name des Properties in {@link SelectItem} für den Model-Value.
     */
    @Input() modelValue: any;
    /**
     * Kann genutzt werden, um dem Reset-Item einen individuellen Anzeigewert zu geben,
     * wenn {@link #type} gleich {@link InputType.select} ist..
     */
    @Input() resetText: string;
    /**
     * Ob ein Reset-Item angezeigt wird. Nur wenn {@link #type} gleich {@link InputType.select} ist.
     */
    @Input() disableReset: boolean = false;
    /**
     * Der Text für das Info-Icon.
     */
    @Input() dataToolTip: string;
    /**
     * Ob das Input-Item disabled ist.
     */
    @Input() disabled: boolean = false;
    /**
     * Ob eine Scrollbar bei einer Selectbox gezeigt werden soll.
     * Nur wenn {@link #type} gleich {@link InputType.select} ist.
     */
    @Input() showScrollbar = false;
    /**
     * Wenn das Label selbst leer ist, die angezeigte Komponente (checkbox) aber ein "title"-Attribut erhalten soll
     */
    @Input() labelTitle: string = '';
    /**
     * Array der Model-Values von gedisabelten {@link RadioButtonItems}.
     * Nur wenn {@link #type} gleich {@link InputType.select} ist.
     */
    @Input() disabledItems: string[] = [];
    /**
     * Nur wenn {@link #type} gleich {@link InputType.select} ist. Ob ein exisitierender Wert im Model auch dann angezeigt
     * werden soll, wenn er in den {@link #items} nicht mehr vorkommt. In den Fällen kann kein "displayValue" ermittelt
     * werden kann, darum wird stattdessen der "modelValue" in Klammern dargestellt. Ist diese Option nicht gesetzt, dann
     * wird gar nichts dargestellt (Eingabefeld bleibt leer trotz gesetztem Wert im Model)
     */
    @Input() showInvalidModelValues: boolean;
    /**
     * Gibt an, wieviel Zeilen die textarea haben soll. (Default=2)
     */
    @Input() textareaRows: number = 2;
    /**
     * Readonly für Textareas.
     * Nur wenn {@link #type} gleich {@link InputType.textarea} ist.
     */
    @Input() readonly: boolean = false;
    // Ein Eingabefeld kann optional als numerisch deklariert werden (default: false). Die ggf. sprachabhängigen Eingaben
    // werden vor der Anzeige in ein generisches Zahlenformat überführt (z.B. Umwandlung , in .). Zusätzlich können optional
    // das Format und der Suffix übergeben werden
    @Input() numeric = false;
    @Input() numericFormat = '1.2-2';
    @Input() numericSuffix = null;
    /**
     * Gibt ein Event zurück, wenn eine CheckBox oder ein Radio-Button ausgewählt wurde.
     */
    @Output() isChecked = new EventEmitter();
    /**
     * Gibt ein Event zurück, wenn eine SelectBox ausgewählt wurde.
     */
    @Output() isSelected = new EventEmitter();
    @ContentChild('afterInput') afterInput;
    @ContentChild('beforeInput') beforeInput;

    /*
        Nach langer Recherche war klargestellt, dass neue Werte, die durch GroupForm oder NgModel gesetzt wurden,
        aktualisieren die Darstellung des Input-Element nicht. Es hängt irgentwie mit setValue-Getter zusammen,
        aber es ist zu gefährlich einfach die "set value" Methode hinzufügen. Um alte Logik nicht zu brechen,
        sind neue Änderungen verkapselt, bzw. die neue Variable "textValue" hinzufügen.
        Ziel für später sollte sein, die normale Text-Input Komponente und die Select- und Checkbox besser voneinander zu
        trennen, denn gerade die Vermischung der Logik führt immer wieder zu Fehlern.
        Ein separates Feld textValue ist ein erster Schritt in diese Richtung.
     */
    textValue: string;

    // File Content will be read as UTF8
    // if type === 'file' this will hold the content
    fileValue: string;

    showReset: boolean = false;
    uniqueId: string;
    isText: boolean;
    isBox: boolean;
    isSelect: boolean;
    isTextarea: boolean;
    isFile: boolean;
    noItems: boolean;
    i18n: any;
    selectionLabel: string;
    keysForBox = [];

    private readonly languageSubscription: Subscription;

    // Placeholders for callbacks which are later provided by ControlValueAccessor
    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    // Internes Model
    private innerValue: any;

    // Unbearbeiteter Wert, der in die writeValue landet.
    private rawValue: any;

    constructor(private languageService: LanguageService,
                private numberPipe: CustomNumberPipe,
                private log: LoggingService) {
        this.languageSubscription = languageService.registerForLanguageChanged(this, 'i18n', I18N);
    }

    ngOnInit() {
        this.setType();

        if (this.isBox && !this.items) {
            this.noItems = true;
        } else if (this.isBox && this.items) {
            this.keysForBox = Object.keys(this.items);
        }
        this.uniqueId = Math.random().toString(16);

        if (this.resetText === undefined || this.resetText === null) {
            this.resetText = this.i18n.formInputComponent.placeholder;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // In dem Fall, wenn die Items async nach dem Wert geliefert werden, soll den Wert auch berücksichtigt werden.
        if (this.type === InputType.select && changes.hasOwnProperty('items') && this.items && this.rawValue) {
            this.writeValue(this.rawValue);
        }
    }

    ngOnDestroy() {
        if (this.languageSubscription) this.languageSubscription.unsubscribe();
    }

    // Getter für den Wert von internem Model
    get value(): any {
        return this.innerValue;
    }

    // From ControlValueAccessor interface
    writeValue(value: any) {
        this.rawValue = value;
        if (this.type === InputType.text || this.type === InputType.textarea || this.type === InputType.password) {
            if (value !== this.textValue) {
                if (this.numeric) {
                    this.textValue = this.numberPipe.transform(value, this.numericFormat, this.numericSuffix, true);
                } else {
                    this.textValue = value;
                }
            }
            return;
        }

        // Items sind nötig um den Wert zu mappen.
        // Dank rawValue werden diese methode noch einmal aufgerufen, sobald die Items verfügbar sind.
        if (this.type === InputType.select && (!this.items || this.items.length === 0)) {
            return;
        }

        if (value === null || value === undefined) {
            this.innerValue = null;
            this.selectionLabel = null;
        } else if (this.modelValue) {
            // Select-Branch für Items vom Typ Array
            this.writeValueForArray(value);
        } else {
            this.writeValueForKey(value);
        }
    }

    // From ControlValueAccessor interface
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    // From ControlValueAccessor interface
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    // From ControlValueAccessor interface
    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    // Für Typ File
    onFileInput(event: Event) {
        const file = event.target['files'][0];
        const fr = new FileReader();
        fr.addEventListener('load', (e: ProgressEvent) => {
            const value = e.target['result'];
            this.onChangeCallback(value);
        });
        fr.readAsText(file, 'utf8');
    }

    // Für Typ Text
    onInput(event: Event) {
        let value = (<HTMLInputElement> event.target).value;
        if (this.numeric) {
            value = value
                // Leerzeichen entfernen
                .replace(/\s/g, '')
                // Punkte entfernen
                .replace(/\./g, '')
                // Komma in Punkte umwandeln
                .replace(',', '.');
        }
        this.onChangeCallback(value);
    }

    // Für Typ Textarea
    onInputTextarea(event: Event) {
        const value = (<HTMLTextAreaElement> event.target).value;
        this.onChangeCallback(value);
    }

    // Für Typ Box: Hotfix für multi-select
    onBoxChange(key?: any) {
        if (key) {
            this.onChangeCallback(key);
            this.innerValue = key;
            this.isChecked.emit(key);
        } else {
            this.innerValue = !this.value;
            this.onChangeCallback(this.innerValue);
            this.isChecked.emit(this.innerValue);
        }
    }

    onSelected(item: any) {
        if (typeof item === 'string') {
            this.selectionLabel = this.items[item];
            this.onChangeCallback(item);
            this.isSelected.emit(item);
            this.innerValue = item;
        } else {
            this.selectionLabel = item[this.displayValue];
            this.onChangeCallback(item[this.modelValue]);
            this.isSelected.emit(item[this.modelValue]);
            this.innerValue = item[this.modelValue];
        }

        if (this.disableReset === false) {
            this.showReset = true;
        }
    }

    onResetSelection() {
        this.selectionLabel = this.resetText;
        this.onChangeCallback(null);
        this.isSelected.emit(null);
        this.innerValue = null;

        if (this.disableReset === false) {
            this.showReset = false;
        }
    }

    /**
     * Für Fälle in denen die items als Typ Array hineingegeben werden und
     * im internen Model der Wert des mit 'modelValue' gewählten Keys gespeichert werden muss.
     * @param value {(Object|string)}
     */
    private writeValueForArray(value: any) {
        if (typeof value === 'string' || typeof value === 'number') {
            if (value !== this.value) {
                const getModelValue = (entity) => {
                    return entity[this.modelValue] === value;
                };
                const entity = (this.items as SelectItem[]).find(getModelValue) ||
                    this.showInvalidModelValues && {
                        [this.modelValue || 'id']: value,
                        [this.displayValue || 'value']: '(' + value + ')'
                    };
                if (entity) {
                    this.innerValue = entity[this.modelValue];
                    this.selectionLabel = entity[this.displayValue] as string;
                }
            }
        } else { // value ist hier das selektierte Entitäts-Objekt
            if (value[this.modelValue] !== this.value) {
                this.innerValue = value[this.modelValue];
                this.selectionLabel = value[this.displayValue];
            }
        }

        if (this.disableReset === false) {
            this.showReset = true;
        }
    }

    /**
     * Für Fälle in denen die items als Typ JSON hineingegeben werden und
     * lediglich ein key im internen Model gespeichert werden muss.
     * @param value {(string|number)}
     */
    private writeValueForKey(value: any) {
        if (value !== this.innerValue) {
            this.innerValue = value;
            if (this.isSelect) {
                this.selectionLabel = this.items[value];
            }
        }

        if (this.disableReset === false) {
            this.showReset = true;
        }
    }

    private setType() {
        switch (this.type) {
            case InputType.radio:
            case InputType.checkbox:
                this.isBox = true;
                break;
            case InputType.select:
                this.isSelect = true;
                break;
            case InputType.textarea:
                this.isTextarea = true;
                break;
            case InputType.file:
                this.isFile = true;
                break;
            case InputType.text:
            case InputType.password:
                this.isText = true;
                break;
            default:
                this.isText = true;
                // ist default wert
                this.type = InputType.text;
                break;
        }
        // this.log.debug(`FormInputComponent#setType: this.type=${this.type}`);
    }
}
